import { useMemo, useState } from "react";
import GameShell from "../GameShell";

/**
 * Caverna dos Ecos — ABFW-style vocabulary task.
 * 10 trials: show 4 emoji pictures + display target word; tap matching picture.
 */
export default function EchoCave() {
  return (
    <GameShell gameId="echo_cave" title="Caverna dos Ecos">
      {({ onFinish }) => <Game onFinish={onFinish} />}
    </GameShell>
  );
}

const ITEMS = [
  { word: "Maçã", emoji: "🍎" },
  { word: "Cachorro", emoji: "🐶" },
  { word: "Estrela", emoji: "⭐" },
  { word: "Sol", emoji: "☀️" },
  { word: "Livro", emoji: "📕" },
  { word: "Carro", emoji: "🚗" },
  { word: "Banana", emoji: "🍌" },
  { word: "Gato", emoji: "🐱" },
  { word: "Bola", emoji: "⚽" },
  { word: "Árvore", emoji: "🌳" },
  { word: "Casa", emoji: "🏠" },
  { word: "Peixe", emoji: "🐟" },
  { word: "Avião", emoji: "✈️" },
  { word: "Flor", emoji: "🌸" },
];

const TRIALS = 10;

function pickTrial(prevIdx) {
  let target;
  do {
    target = ITEMS[Math.floor(Math.random() * ITEMS.length)];
  } while (prevIdx && target.word === prevIdx);
  const pool = ITEMS.filter((it) => it.word !== target.word);
  const distractors = pool.sort(() => Math.random() - 0.5).slice(0, 3);
  const opts = [target, ...distractors].sort(() => Math.random() - 0.5);
  return { target, opts };
}

function Game({ onFinish }) {
  const [trialIdx, setTrialIdx] = useState(0);
  const [data, setData] = useState(() => pickTrial());
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [tStart, setTStart] = useState(() => performance.now());
  const [started] = useState(() => performance.now());
  const [feedback, setFeedback] = useState(null);

  const choose = (it) => {
    if (feedback) return;
    const rt = performance.now() - tStart;
    const correct = it.word === data.target.word;
    setReactionTimes((r) => [...r, rt]);
    if (correct) setHits((h) => h + 1);
    else setMisses((m) => m + 1);
    setFeedback(correct ? "✅" : "❌");
    setTimeout(() => {
      const next = trialIdx + 1;
      if (next >= TRIALS) {
        const elapsed = performance.now() - started;
        const totalHits = hits + (correct ? 1 : 0);
        const totalMisses = misses + (correct ? 0 : 1);
        const avg = [...reactionTimes, rt].reduce((a, b) => a + b, 0) / (reactionTimes.length + 1);
        onFinish({
          score: Math.max(0, Math.round(totalHits * 10 - totalMisses * 3)),
          duration_ms: Math.round(elapsed),
          metrics: {
            hits: totalHits,
            misses: totalMisses,
            trials: TRIALS,
            avg_reaction_ms: Math.round(avg),
            accuracy: Number((totalHits / TRIALS).toFixed(3)),
          },
        });
      } else {
        setFeedback(null);
        setTrialIdx(next);
        setData(pickTrial(data.target.word));
        setTStart(performance.now());
      }
    }, 450);
  };

  return (
    <div className="mt-4 max-w-2xl mx-auto" data-testid="echo-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">Toque na figura da palavra mostrada.</div>
        <div className="text-xs mt-1 opacity-80">Rodada {trialIdx + 1} / {TRIALS}</div>
      </div>
      <div className="kid-card pop-in" key={trialIdx}>
        <div className="text-center mb-4">
          <div className="inline-block bg-sky-100 text-sky-900 font-extrabold text-3xl md:text-4xl px-6 py-2 rounded-2xl" data-testid="echo-word">
            {data.target.word}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.opts.map((opt, i) => (
            <button
              key={i}
              onClick={() => choose(opt)}
              className="aspect-square rounded-2xl border-2 border-slate-200 text-6xl flex items-center justify-center bg-sky-50 hover:scale-105 active:scale-95 transition"
              data-testid={`echo-opt-${i}`}
            >
              {opt.emoji}
            </button>
          ))}
        </div>
        {feedback && (
          <div className="text-center text-4xl mt-3 pop-in" data-testid="echo-feedback">
            {feedback}
          </div>
        )}
      </div>
    </div>
  );
}
